/**
 * UrlParam is a utility class for managing URL parameters.
 * It provides a simple interface for getting, setting, and removing URL parameters.
 * It also supports options for pushing or replacing the URL in the history.
 * 
 * @typedef {Object} UrlParamOptions
 * @property {boolean} [push=false] - If true, it uses history.pushState
 */

class UrlParam {
  constructor() {
    this.params = this.#getParams()

    /** @type {UrlParamOptions} */
    this.defaultOptions = { push: false }
  }

  /**
   * 
   * @returns {URLSearchParams}
   */
  #getParams() {
    return new URLSearchParams(window.location.search)
  }

  /**
   * Get the value of an URL param
   * @param {string} name 
   * @returns {string}
  */
  get(name) {
    this.params = this.#getParams()
    return this.params.get(name) || ''
  }

  /**
   * Set an URL param
 * @typedef {Object} UrlParamItem
 * @property {string} name
 * @property {string} value
 * @property {UrlParamOptions} [options]
  */
  set(name, value, options) {
    if (!name) return
    this.params.set(name, value)
    this.#updateUrl(options)
  }
  
  /**
   * Remover a parameter from the url
   * @param {string} name
   * @param {UrlParamOptions} [options]
  */
  remove(name, options) {
    if (!name) return
    this.params.delete(name)
    this.#updateUrl(options)
  }

  /**
   * Set multiple params at once
   * @param {UrlParamItem[]} params
   */
  bulk(params) {
    if (!params || !Array.isArray(params)) return

    params.forEach(param => {
      const {name, value, options} = param

      if (name) {
        if (value) {
          // set param if it has value
          this.params.set(name, value, options)
        } else {
          // remove param if it doesn't has value 
          this.params.delete(name)
        }
      }
    })

    this.#updateUrl()
  }

  #updateUrl(options) {
    options = { ...this.defaultOptions, ...options }
    const search = this.params.toString()
    const fullUrl = search ? `${window.location.pathname}?${search}` : window.location.pathname

    window.history.replaceState({}, '', fullUrl)
  }
}

const urlParam = new UrlParam()
export default urlParam
